/**
 * The sync indicator: a row in the sidebar, a dot in the phone's top bar.
 *
 * Six states, one line of text and a dot. The dot exists because the collapsed
 * sidebar and the phone's bar have room for nothing else, and because "is my
 * work safe?" is a glanceable question, not a readable one. Two mounts of one
 * component: the drawer says it in words, the bar says it in a dot
 * (PLAN-UX-PASS F5).
 *
 * `offline` is deliberately not styled as an error: edits still apply, still
 * persist and still queue, and the client is already trying to reconnect. The
 * only genuinely bad state would be losing work, which the outbox prevents.
 *
 * ## Why it is a button
 *
 * Every state but `guest` and `stale` is a tap that throws the socket away and
 * opens a new one. The client bounds its own waiting now, so a tap is rarely
 * the thing that fixes a connection — but the shell turns off pull-to-refresh
 * (`styles.css`), so on a phone the alternative to this button is hunting
 * through the browser menu for a reload, and a brewer who thinks the app is
 * stuck deserves something to press. `syncing` also carries the queue length,
 * because a number that stops moving is how "stuck" tells itself apart from
 * "busy", and it is the one thing a brewer reporting a problem can read to us.
 *
 * `stale` is the exception, and its button says `Reload` instead. A deploy
 * moved the sync protocol and this tab is still on the old bundle; the client has stopped reconnecting
 * because every attempt would be the same refusal, and only a reload — which
 * the brewer has to choose, because it can eat a half-typed note — can follow
 * the server. The deploy's own quiet-moment reload (`lib/shell-update.ts`)
 * usually gets there first; this is the honest badge for the minutes before it
 * does. Reconnecting there would do nothing — the client has given up on
 * purpose — so that state keeps the reload it already had, and nothing is
 * nested inside anything. Collapsed there is no second control to nest, so the
 * dot itself is the reload. The view is split from the hook so the states can be
 * rendered in a test without a sync client behind them.
 */

import type { LucideIcon } from 'lucide-react';
import { CloudIcon, CloudOffIcon, LoaderIcon, RefreshCwIcon, UserRoundIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SyncStatus } from '@/sync/sync-client';
import { useSyncClient, useSyncPendingCount, useSyncStatus } from '@/sync/sync-context';

interface Presentation {
  readonly label: string;
  readonly detail: string;
  readonly icon: LucideIcon;
  readonly dot: string;
  readonly spin?: boolean;
  /**
   * Added to the collapsed button's label, where there is no visible text.
   *
   * Only for the states whose `detail` does not already invite the tap in
   * words fitted to what is happening; saying it twice would be worse for the
   * one reader who hears the whole string.
   */
  readonly tap?: string;
}

const PRESENTATION: Readonly<Record<SyncStatus, Presentation>> = {
  guest: {
    label: 'Guest',
    detail: 'Saved on this device only',
    icon: UserRoundIcon,
    dot: 'bg-muted-foreground/50',
  },
  connecting: {
    label: 'Connecting',
    detail: 'Opening the sync connection',
    icon: LoaderIcon,
    dot: 'bg-amber-500',
    spin: true,
    tap: 'Tap to reconnect.',
  },
  syncing: {
    label: 'Syncing',
    detail: 'Sending your latest changes. Tap to reconnect if this is taking a while.',
    icon: RefreshCwIcon,
    dot: 'bg-amber-500',
    spin: true,
  },
  synced: {
    label: 'Synced',
    detail: 'Everything is saved to your account',
    icon: CloudIcon,
    dot: 'bg-emerald-500',
    tap: 'Tap to reconnect.',
  },
  offline: {
    label: 'Offline',
    detail: "Your changes are queued and we'll send them when you're back. Tap to try now.",
    icon: CloudOffIcon,
    dot: 'bg-muted-foreground',
  },
  stale: {
    label: 'Reload needed',
    detail:
      'This tab is on an older version of Beerwright than your account now syncs with. Your edits are safe on this device; reload and we will send them.',
    icon: RefreshCwIcon,
    dot: 'bg-amber-500',
    // The detail asks for a reload without saying what will perform one, which
    // is a complete sentence beside the expanded row's `Reload` button and an
    // unanswered question beside a bare dot. Collapsed, this is the answer, and
    // it says "now" because the tap reloads immediately rather than offering.
    tap: 'Tap to reload now.',
  },
};

/** Status text plus a coloured dot. `collapsed` drops to the dot alone. */
export function SyncStatusIndicator({ collapsed }: { readonly collapsed: boolean }) {
  const status = useSyncStatus();
  const pending = useSyncPendingCount();
  const client = useSyncClient();
  return (
    <SyncStatusView
      status={status}
      collapsed={collapsed}
      pending={pending}
      onReconnect={() => client?.reconnect()}
    />
  );
}

/**
 * The indicator for one given status.
 *
 * `onReload` and `onReconnect` both default to doing nothing useful and are
 * props so a test can see the button pressed; only {@link SyncStatusIndicator}
 * passes a real reconnect.
 */
export function SyncStatusView({
  status,
  collapsed,
  pending = 0,
  onReload = () => window.location.reload(),
  onReconnect = () => undefined,
}: {
  readonly status: SyncStatus;
  readonly collapsed: boolean;
  /** How many commands are waiting. Shown while `syncing`, where it is the news. */
  readonly pending?: number;
  readonly onReload?: () => void;
  readonly onReconnect?: () => void;
}) {
  const { label: name, detail, icon: Icon, dot, spin, tap } = PRESENTATION[status];
  const label = status === 'syncing' && pending > 0 ? `${name} · ${pending}` : name;
  // Guest has no socket, and stale has given up on purpose; everything else is
  // a connection a tap can throw away and open again.
  const reconnectable = status !== 'guest' && status !== 'stale';

  if (collapsed) {
    const body = (
      <>
        <span className={cn('size-2 rounded-full', dot)} aria-hidden="true" />
        <span className="sr-only">{label}</span>
      </>
    );
    // Collapsed, `stale` is a button too, and its tap is the reload rather than
    // a reconnect (PLAN-UX-PASS F5). The expanded row can afford a `Reload`
    // beside the words; the phone's top bar carries this form and nothing else,
    // and a dot with nothing behind it would leave the one state whose whole
    // point is "only a reload can follow the server" with no way to ask for one.
    // Guest is still inert, because there is nothing there to act on.
    const act = status === 'stale' ? onReload : onReconnect;
    if (status === 'guest') {
      return (
        <div
          className="flex h-7 items-center justify-center"
          title={`${label} — ${detail}`}
          data-sync-status={status}
        >
          {body}
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={act}
        aria-label={[`${label}.`, detail, tap].filter((part) => part !== undefined).join(' ')}
        className="flex h-7 w-full cursor-pointer items-center justify-center rounded-md hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        title={`${label} — ${detail}`}
        data-sync-status={status}
      >
        {body}
      </button>
    );
  }

  const row = (
    <>
      <Icon
        className={cn('size-3.5 shrink-0 text-muted-foreground', spin === true && 'animate-spin')}
        aria-hidden="true"
      />
      <span className="truncate text-xs text-muted-foreground">{label}</span>
    </>
  );

  if (reconnectable) {
    return (
      <button
        type="button"
        onClick={onReconnect}
        className="flex w-full cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-left hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        title={detail}
        data-sync-status={status}
      >
        {row}
        <span className={cn('ml-auto size-2 shrink-0 rounded-full', dot)} aria-hidden="true" />
      </button>
    );
  }

  return (
    <div
      className="flex items-center gap-2 rounded-md px-1.5 py-1"
      title={detail}
      data-sync-status={status}
    >
      {row}
      {status === 'stale' ? (
        <button
          type="button"
          onClick={onReload}
          className="ml-auto rounded-sm px-1.5 text-xs font-medium text-foreground underline-offset-2 hover:underline"
        >
          Reload
        </button>
      ) : (
        <span className={cn('ml-auto size-2 shrink-0 rounded-full', dot)} aria-hidden="true" />
      )}
    </div>
  );
}
